import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { authService } from './services/auth';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import Suppliers from './pages/Suppliers';
import PurchaseRequisitions from './pages/PurchaseRequisitions';
import RFQs from './pages/RFQs';
import RFQDetail from './pages/RFQDetail';
import History from './pages/History';
import SavingsDashboard from './pages/SavingsDashboard';
import TATReport from './pages/TATReport';
import Settings from './pages/Settings';
import UserManagement from './pages/UserManagement';
import AdminDashboard from './pages/AdminDashboard';
import QuoteSubmission from './pages/QuoteSubmission';
import AcceptInvite from './pages/AcceptInvite';
import Login from './pages/Login';
import Signup from './pages/Signup';
import AuthCallback from './pages/AuthCallback';
import './App.css';

const ProtectedRoute = ({ children }) => {
  const location = useLocation();
  
  // If user data passed from AuthCallback, they are authenticated
  if (location.state?.user) {
    return children;
  }
  
  if (!authService.isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

const PublicRoute = ({ children }) => {
  if (authService.isAuthenticated()) {
    return <Navigate to="/" replace />;
  }
  return children;
};

// Main App Router - handles OAuth callback detection
const AppRouter = () => {
  const location = useLocation();
  
  // CRITICAL: Check for session_id synchronously during render to prevent race conditions
  // This handles Emergent-managed Google OAuth callback
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/quote/:token" element={<QuoteSubmission />} />
      <Route path="/accept-invite" element={<AcceptInvite />} />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/signup"
        element={
          <PublicRoute>
            <Signup />
          </PublicRoute>
        }
      />

      {/* Protected routes */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="suppliers" element={<Suppliers />} />
        <Route path="purchase-requisitions" element={<PurchaseRequisitions />} />
        <Route path="rfqs" element={<RFQs />} />
        <Route path="rfqs/:id" element={<RFQDetail />} />
        <Route path="history" element={<History />} />
        <Route path="savings" element={<SavingsDashboard />} />
        <Route path="tat-report" element={<TATReport />} />
        <Route path="settings" element={<Settings />} />
        <Route path="team" element={<UserManagement />} />
      </Route>
    </Routes>
  );
};

function App() {
  return (
    <>
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster position="top-right" />
    </>
  );
}

export default App;
